import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { copyToClipboard as clipboardCopy } from "@/lib/clipboard";
import { toast } from "sonner";
import VaultCredentialsCard from "./VaultCredentialsCard";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import {
  ShieldAlert, BarChart3, Coins, ReceiptText, FileText, Info,
  Download, Calendar, AlertCircle, Loader2, CheckCircle2, ExternalLink, RefreshCw, Send,
  Folder, ChevronDown, Trash2, AlertTriangle,
} from "lucide-react";

// Format a Date object as YYYY-MM-DD in LOCAL time (avoid UTC drift on Italian timezone)
const fmtLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Compute the Alloggiati Web 24h deadline for a guest (returns hours-left or null if past).
const alloggiatiHoursLeft = (checkedInAt) => {
  if (!checkedInAt) return null;
  const ci = new Date(checkedInAt).getTime();
  if (isNaN(ci)) return null;
  const deadline = ci + 24 * 60 * 60 * 1000;
  const diffMs = deadline - Date.now();
  return Math.round(diffMs / (60 * 60 * 1000)); // hours, can be negative if expired
};

const DEFAULT_FROM = () => {
  const d = new Date();
  d.setDate(1);
  return fmtLocal(d);
};
const TODAY = () => fmtLocal(new Date());

const REPORTS = [
  {
    key: "alloggiati",
    label: "Schede Alloggiati Polizia di Stato",
    short: "Polizia di Stato",
    desc: "File .txt conforme al portale Alloggiati Web. Va caricato entro 24h dal check-in.",
    icon: ShieldAlert,
    color: "rose",
    format: "TXT",
    legalNote: "Obbligo D.M. 7/1/2013 — entro 24h dall'arrivo dell'ospite.",
    portal: { url: "https://alloggiatiweb.poliziadistato.it/PortaleAlloggiati/", label: "Apri Alloggiati Web" },
  },
  {
    key: "istat",
    label: "ISTAT Movimentazione Clienti",
    short: "ISTAT",
    desc: "Riepilogo mensile arrivi/presenze per nazionalità da inviare al Servizio ISTAT.",
    icon: BarChart3,
    color: "blue",
    format: "CSV / PDF",
    legalNote: "Reg. CE n. 692/2011 — invio mensile entro il 5 del mese successivo.",
    portal: { url: "https://sirdat.regione.calabria.it/", label: "Apri SIRDAT (ROSS1000)" },
  },
  {
    key: "tassa-soggiorno",
    label: "Modello A1 — Tassa di Soggiorno Trimestrale",
    short: "Modello A1 (T1/T2/T3/T4)",
    desc: "Rendiconto trimestrale per il Comune di Reggio Calabria. Scadenze: T1=15/04, T2=15/07, T3=15/10, T4=15/01 anno successivo. Trasmissione tramite portale comunale ufficiale.",
    icon: Coins,
    color: "amber",
    format: "PDF",
    legalNote: "Regolamento Comunale RC — €1,00/persona/notte (max 5 notti, esenti <14 anni, residenti RC, disabili).",
    portal: { url: "https://tassadisoggiorno.reggiocal.it/", label: "Apri portale Tassa Soggiorno RC" },
  },
  {
    key: "mef-annual",
    label: "Dichiarazione Annuale Telematica MEF",
    short: "MEF annuale",
    desc: "Tracciato fixed-length 1900 byte (record A/B/C/D/E/Z) da inviare via Entratel/Fisconline entro il 30/06 dell'anno successivo.",
    icon: FileText,
    color: "purple",
    format: "TXT",
    legalNote: "D.M. 11/11/2014 — invio annuale entro il 30 giugno per l'anno fiscale precedente.",
    portal: { url: "https://telematici.agenziaentrate.gov.it/", label: "Apri Entratel/Fisconline" },
  },
];

const COLOR_MAP = {
  rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", icon: "bg-rose-100 text-rose-700" },
  blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: "bg-blue-100 text-blue-700" },
  amber: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", icon: "bg-amber-100 text-amber-700" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: "bg-emerald-100 text-emerald-700" },
  purple: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", icon: "bg-purple-100 text-purple-700" },
};

export default function ComplianceTab({ onGoToBookings } = {}) {
  const [fromDate, setFromDate] = useState(DEFAULT_FROM());
  const [toDate, setToDate] = useState(TODAY());
  const [downloading, setDownloading] = useState(null);
  // Per-kind archive of previously generated reports (list, no binary)
  const [archives, setArchives] = useState({}); // { alloggiati: [...], istat: [...] }
  const [expandedArchive, setExpandedArchive] = useState(null); // currently-opened card key
  const [clientsListOpen, setClientsListOpen] = useState(false); // collapsible client list (default closed per user req)
  const [periodPickerOpen, setPeriodPickerOpen] = useState(true); // collapsible period picker

  const loadArchive = useCallback(async (key) => {
    try {
      const res = await api.get(`/admin/reports/archive/${key}`);
      setArchives((prev) => ({ ...prev, [key]: res.data.items || [] }));
    } catch {
      setArchives((prev) => ({ ...prev, [key]: [] }));
    }
  }, []);

  // Load all 4 archives on mount
  useEffect(() => {
    REPORTS.forEach((r) => loadArchive(r.key));
  }, [loadArchive]);

  const downloadFromArchive = async (kind, file) => {
    try {
      const res = await api.get(`/admin/reports/archive/${kind}/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${file.filename} scaricato`);
    } catch {
      toast.error("File non disponibile");
    }
  };

  const deleteFromArchive = async (kind, file) => {
    if (!window.confirm(`Eliminare "${file.filename}" dall'archivio?`)) return;
    try {
      await api.delete(`/admin/reports/archive/${kind}/${file.id}`);
      await loadArchive(kind);
      toast.success("File rimosso dall'archivio");
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const downloadReport = async (key) => {
    if (!fromDate || !toDate) return toast.error("Seleziona il periodo");
    if (fromDate > toDate) return toast.error("Date non valide");

    // 🗂️ Anti-duplicate: if an archive file already exists for this exact period, reuse it.
    const existing = (archives[key] || []).find(
      (f) => f.from_date === fromDate && f.to_date === toDate,
    );
    if (existing) {
      toast.message(`File già in archivio per ${fromDate} → ${toDate}`, {
        description: "Lo scarico dall'archivio senza rigenerare un duplicato.",
      });
      await downloadFromArchive(key, existing);
      setExpandedArchive(key);
      return;
    }

    setDownloading(key);
    try {
      const res = await api.get(`/admin/reports/${key}`, {
        params: { from: fromDate, to: toDate },
        responseType: "blob",
        // PDF generation (especially "ricevute") can be CPU-heavy.
        // Allow up to 60s before failing; Cloudflare still caps at ~100s.
        timeout: 60_000,
      });
      const blob = res.data;
      // Guard: empty file means no qualifying bookings in the period.
      // For CSV reports a "header-only" file is also effectively empty.
      const size = blob?.size || 0;
      if (size === 0 || (size > 0 && size < 120 && /csv|text|plain/.test(blob.type))) {
        toast.message("Nessun dato per il periodo selezionato", {
          description: "Per questo periodo non risultano prenotazioni o check-in da includere nel report. Prova un periodo diverso (es. mese scorso).",
          duration: 6000,
        });
        return;
      }
      // Filename from Content-Disposition or fallback
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match ? match[1] : `${key}_${fromDate}_${toDate}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${REPORTS.find((r) => r.key === key)?.short} scaricato`);

      // Reload the archive of this kind so the new file appears
      await loadArchive(key);
      // Auto-expand the archive panel so the user sees the new file
      setExpandedArchive(key);

      // ✨ Auto-mark all clients in the period as "communicated" to the corresponding authority.
      // Skip "ricevute" (it's just guest receipts, not a compliance authority).
      const KIND_MAP = { alloggiati: "alloggiati", istat: "istat", "tassa-soggiorno": "tassa" };
      const kind = KIND_MAP[key];
      if (kind && clientsList.length > 0) {
        const ids = clientsList.map((c) => c.id);
        try {
          await api.post("/admin/compliance/mark", { ids, kind, mark: true });
          await refreshBookingsCount();
          const label = kind === "alloggiati" ? "POLIZIA" : kind === "istat" ? "ISTAT" : "TASSA";
          toast.message(`✓ ${ids.length} client${ids.length === 1 ? "e" : "i"} marcat${ids.length === 1 ? "o" : "i"} come comunicat${ids.length === 1 ? "o" : "i"}`, {
            description: `Il badge "✓ ${label}" è stato applicato automaticamente.`,
          });
        } catch {
          // silent fail — download succeeded, marking is best-effort
        }
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 501) {
        toast.warning(
          "Funzionalità non ancora disponibile",
          { description: "Il report sarà disponibile a breve." }
        );
      } else if (status === 502 || status === 504) {
        toast.error(
          "App Guida Digitale temporaneamente offline",
          { description: "L'integrazione è configurata ma l'app non risponde. Riprova tra qualche istante." }
        );
      } else {
        toast.error(errMsg(err, "Errore download"));
      }
    } finally {
      setDownloading(null);
    }
  };

  // Live health check of the Digital Guide integration (used to show status badge).
  const [guideStatus, setGuideStatus] = useState("checking"); // checking | online | offline
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await api.get("/admin/guide/status");
        if (!cancelled) {
          const ok = !!res.data?.configured && !!res.data?.health?.ok;
          setGuideStatus(ok ? "online" : "offline");
        }
      } catch {
        if (!cancelled) setGuideStatus("offline");
      }
    };
    check();
    const id = setInterval(check, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Live counter of confirmed/completed bookings for the selected period
  const [bookingsCount, setBookingsCount] = useState({ total: 0, eligible: 0, standalone: 0, loading: true });
  const [pullStatus, setPullStatus] = useState({ pulling: false, lastUpdated: 0, lastPulledAt: null });
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [webhookCopied, setWebhookCopied] = useState("");
  const [syncCodesOpen, setSyncCodesOpen] = useState(false);
  // Walk-in bookings pending room/price assignment
  const [walkinPending, setWalkinPending] = useState([]);
  const [walkinModal, setWalkinModal] = useState(null); // booking obj being assigned
  const [walkinRooms, setWalkinRooms] = useState([]);

  const loadWalkinPending = useCallback(async () => {
    try {
      const r = await api.get("/admin/bookings/walk-in/pending");
      setWalkinPending(r.data?.items || []);
    } catch {
      setWalkinPending([]);
    }
  }, []);

  useEffect(() => {
    loadWalkinPending();
    // Refresh on focus
    const onFocus = () => loadWalkinPending();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadWalkinPending]);

  // Load rooms list for the assign-modal dropdown (lazy on modal open)
  const openAssignRoomModal = async (b) => {
    if (walkinRooms.length === 0) {
      try {
        const r = await api.get("/rooms");
        setWalkinRooms(r.data || []);
      } catch {
        // best-effort
      }
    }
    setWalkinModal({
      ...b,
      _selected_room_id: "",
      _price_per_night: "",
      _guests: b.guests || 1,
      _nights: b.nights || 1,
    });
  };

  const submitAssignRoom = async () => {
    if (!walkinModal) return;
    const rid = walkinModal._selected_room_id;
    if (!rid) { toast.error("Seleziona una camera"); return; }
    try {
      await api.post(`/admin/bookings/${walkinModal.id}/assign-room`, {
        room_id: rid,
        room_name: walkinModal._selected_room_name || undefined,
        price_per_night: parseFloat(walkinModal._price_per_night || 0) || 0,
        guests: parseInt(walkinModal._guests, 10) || 1,
        nights: parseInt(walkinModal._nights, 10) || 1,
      });
      toast.success(`Camera assegnata a ${[walkinModal.guest?.first_name, walkinModal.guest?.last_name].filter(Boolean).join(" ") || "ospite"}`);
      setWalkinModal(null);
      await loadWalkinPending();
      await refreshBookingsCount();
    } catch (e) {
      toast.error(errMsg(e, "Errore assegnazione camera"));
    }
  };

  // Load webhook info on mount (used to show configuration banner when no events yet)
  const [recentEvents, setRecentEvents] = useState([]);
  const [webhookLogOpen, setWebhookLogOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const loadInfo = async () => {
      try {
        const [infoRes, evRes] = await Promise.all([
          api.post("/admin/guide/webhook-info"),
          api.get("/admin/guide/events", { params: { limit: 5 } }),
        ]);
        if (cancelled) return;
        setWebhookInfo(infoRes.data);
        setRecentEvents(evRes.data?.events || []);
      } catch {
        // best-effort
      }
    };
    loadInfo();
    return () => { cancelled = true; };
  }, []);

  const copyToClipboard = async (text, label) => {
    const ok = await clipboardCopy(text);
    if (ok) {
      setWebhookCopied(label);
      setTimeout(() => setWebhookCopied(""), 2000);
    } else {
      toast.error("Impossibile copiare");
    }
  };

  // Compliance "marca come comunicato" — bulk action
  const [marking, setMarking] = useState(false);
  // List of bookings + standalone check-ins in the selected period (for visual confirmation)
  const [clientsList, setClientsList] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState(new Set());

  const markCompliance = async (kind, mark = true) => {
    if (selectedCodes.size === 0) {
      toast.error("Seleziona almeno un cliente");
      return;
    }
    setMarking(true);
    try {
      const ids = Array.from(selectedCodes);
      await api.post("/admin/compliance/mark", { ids, kind, mark });
      toast.success(`✓ ${ids.length} client${ids.length === 1 ? "e" : "i"} ${mark ? "marcat" : "smarcati"}${ids.length === 1 ? "o" : "i"} come ${mark ? "comunicat" : "non comunicat"}${ids.length === 1 ? "o" : "i"} (${kind})`);
      await refreshBookingsCount();
    } catch (e) {
      toast.error(errMsg(e, "Errore aggiornamento"));
    } finally {
      setMarking(false);
    }
  };

  // Soft retry mechanism: when both endpoints fail (typically right after
  // deploy when indexes are building), we silently retry once after a delay
  // and only surface a toast on persistent failure.
  const lastToastRef = useRef(0);
  const refreshBookingsCount = useCallback(async (retryCount = 0) => {
    if (!fromDate || !toDate || fromDate > toDate) return;
    const [bookingsResult, checkinsResult] = await Promise.allSettled([
      api.get("/admin/bookings", { params: { from: fromDate, to: toDate } }),
      api.get("/admin/guest-checkins", { params: { from: fromDate, to: toDate } }),
    ]);
    try {
      const bookingsRes = bookingsResult.status === "fulfilled" ? bookingsResult.value : null;
      const checkinsRes = checkinsResult.status === "fulfilled" ? checkinsResult.value : null;
      if (!bookingsRes && !checkinsRes) {
        // First failure → retry silently after 3 seconds. Only surface a
        // toast if the retry also fails, and at most once every 30 seconds
        // to avoid the spam reported in iter111.
        if (retryCount < 1) {
          setTimeout(() => refreshBookingsCount(retryCount + 1), 3000);
          return;
        }
        setBookingsCount({ total: 0, eligible: 0, standalone: 0, loading: false });
        setClientsList([]);
        const now = Date.now();
        if (now - lastToastRef.current > 30_000) {
          lastToastRef.current = now;
          toast.error("Conteggio prenotazioni non disponibile — verifica connessione al backend");
        }
        return;
      }
      const list = bookingsRes
        ? (Array.isArray(bookingsRes.data) ? bookingsRes.data : (bookingsRes.data?.bookings || []))
        : [];
      const eligible = list.filter((b) => ["confirmed", "completed", "checked_in"].includes(b.status)).length;
      const checkins = checkinsRes?.data?.checkins || [];
      const standalone = checkins.length;
      setBookingsCount({ total: list.length + standalone, eligible: eligible + standalone, standalone, loading: false });
      // No toast here: an empty result is a legitimate state (no bookings in
      // the period), not an error. Only the full-failure branch above warns.
      // Build the unified clients list (eligible bookings + standalone check-ins)
      const eligibleBookings = list
        .filter((b) => ["confirmed", "completed", "checked_in"].includes(b.status))
        .map((b) => ({
          kind: "booking",
          id: b.code || b.id,
          name: [b.guest?.first_name, b.guest?.last_name].filter(Boolean).join(" ") || b.guest_name || "Ospite",
          arrival: b.check_in,
          // Effective check-in moment (when guest physically arrived) — used for the 24h Alloggiati deadline
          checked_in_at: b.guest_checked_in_at || (b.check_in ? `${b.check_in}T15:00:00` : null),
          nights: b.nights,
          country: b.guest?.country || b.guest?.nationality || "",
          doc: b.guest?.document_number || "",
          alloggiati_at: b.compliance_alloggiati_at || null,
          istat_at: b.compliance_istat_at || null,
          tassa_at: b.compliance_tassa_at || null,
          // Used below to dedupe standalone check-ins already linked to this booking
          _bookingCode: b.code,
          _externalId: b.external_id,
        }));
      // Set of identifiers already represented by a booking row so we can
      // drop standalone check-ins that are just the App Guida side of the
      // same record. Without this, a Booking reservation that also has 4
      // family-member check-ins would render as 5 lines.
      const bookingKeys = new Set();
      for (const eb of eligibleBookings) {
        if (eb._bookingCode) bookingKeys.add(eb._bookingCode);
        if (eb._externalId) bookingKeys.add(eb._externalId);
      }
      const standaloneRows = checkins
        .filter((c) => {
          // Keep only check-ins that are NOT linked to a booking already
          // present in the list. The linkage can be stored as external_id
          // (= booking code) by guide_pull_checkins / fuzzy backfill.
          const ext = c.external_id;
          if (!ext) return true;
          return !bookingKeys.has(ext);
        })
        .map((c) => ({
          kind: "standalone",
          id: c.id,
          name: [c.guest?.first_name, c.guest?.last_name].filter(Boolean).join(" ") || "Ospite",
          arrival: (c.checked_in_at || "").slice(0, 10),
          checked_in_at: c.checked_in_at || null,
          nights: c.guest?.nights || 1,
          country: c.guest?.country || c.guest?.nationality || "",
          doc: c.guest?.document_number || c.guest?.document || "",
          alloggiati_at: c.compliance_alloggiati_at || null,
          istat_at: c.compliance_istat_at || null,
          tassa_at: c.compliance_tassa_at || null,
        }));
      const combined = [...eligibleBookings, ...standaloneRows].sort((a, b) => {
        // Priority sort: first non-communicated by Polizia, then by hours-to-deadline ASC
        const aDone = !!a.alloggiati_at;
        const bDone = !!b.alloggiati_at;
        if (aDone !== bDone) return aDone ? 1 : -1; // pending first
        if (!aDone) {
          const ah = alloggiatiHoursLeft(a.checked_in_at);
          const bh = alloggiatiHoursLeft(b.checked_in_at);
          if (ah !== null && bh !== null) return ah - bh; // most urgent first
        }
        return (b.arrival || "").localeCompare(a.arrival || ""); // newest arrival first when both done
      });
      setClientsList(combined);
      setSelectedCodes(new Set(combined.map((x) => x.id)));
    } catch {
      setBookingsCount({ total: 0, eligible: 0, standalone: 0, loading: false });
      setClientsList([]);
    }
  }, [fromDate, toDate]);

  const sendTestPing = async () => {
    try {
      const res = await api.post("/admin/guide/test-ping");
      if (res.data?.outgoing_ok) {
        toast.success("✓ Ping inviato · connessione bidirezionale OK");
      } else {
        toast.message("✓ Test ping scritto nel log", {
          description: "L'evento test.ping è stato scritto nel tuo log (uscita verso la Guida non testabile in questo momento).",
        });
      }
      // Reload events to show the new test.ping
      const evRes = await api.get("/admin/guide/events", { params: { limit: 5 } });
      setRecentEvents(evRes.data?.events || []);
      // Reload webhook info to bump total counter
      const infoRes = await api.post("/admin/guide/webhook-info");
      setWebhookInfo(infoRes.data);
    } catch (e) {
      toast.error(errMsg(e, "Errore invio ping"));
    }
  };

  const pullCheckinsFromGuide = useCallback(async (silent = false) => {
    setPullStatus((p) => ({ ...p, pulling: true }));
    try {
      const res = await api.post("/admin/guide/pull-checkins");
      const updated = res.data?.updated || 0;
      setPullStatus({ pulling: false, lastUpdated: updated, lastPulledAt: res.data?.pulled_at });
      if (updated > 0) {
        toast.success(`✓ ${updated} check-in importat${updated === 1 ? "o" : "i"} dalla Guida Digitale`);
        // Refresh bookings count after import
        await refreshBookingsCount();
      } else if (!silent) {
        toast.info("Nessun nuovo check-in da importare");
      }
    } catch (e) {
      setPullStatus((p) => ({ ...p, pulling: false }));
      if (!silent) toast.error(errMsg(e, "Sincronizzazione check-in fallita"));
    }
  }, [refreshBookingsCount]);

  // Auto-pull on tab mount + bookings count refresh
  useEffect(() => {
    let cancelled = false;
    setBookingsCount((p) => ({ ...p, loading: true }));
    refreshBookingsCount();
    // Auto-sync silently from the guide on mount (best-effort, ignore errors)
    if (!cancelled) pullCheckinsFromGuide(true);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  return (
    <section data-testid="compliance-tab" className="space-y-5">
      {/* Header notice */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2A4F7C] text-white rounded-xl p-5 flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display text-xl mb-1">Adempimenti & Report</h3>
            <span
              className={`text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${
                guideStatus === "online"
                  ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/50"
                  : guideStatus === "offline"
                  ? "bg-rose-500/20 text-rose-200 border border-rose-400/50"
                  : "bg-white/15 text-white/70 border border-white/30"
              }`}
              data-testid="guide-status-badge"
              title={guideStatus === "online" ? "Connessione bidirezionale con l'App Guida Digitale attiva" : "App Guida Digitale offline"}
            >
              {guideStatus === "online" && <><CheckCircle2 className="w-3 h-3" /> Guida Digitale connessa</>}
              {guideStatus === "offline" && <><AlertCircle className="w-3 h-3" /> Guida offline</>}
              {guideStatus === "checking" && <><Loader2 className="w-3 h-3 animate-spin" /> Verifica…</>}
            </span>
          </div>
          <p className="text-sm text-white/85 leading-relaxed">
            Genera e scarica i report obbligatori per gli enti: <strong>Polizia di Stato</strong>, <strong>ISTAT</strong> e <strong>Comune</strong> (tassa soggiorno). Seleziona il periodo, clicca <em>Scarica</em>: il file viene generato in tempo reale.
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => {
                if (guideStatus !== "online") {
                  toast.error(
                    "App Guida Digitale non raggiungibile in questo momento. Verifica che il servizio sia online o riprova fra qualche minuto.",
                    { duration: 7000 },
                  );
                  return;
                }
                pullCheckinsFromGuide(false);
              }}
              disabled={pullStatus.pulling}
              data-testid="pull-checkins-btn"
              title={guideStatus !== "online" ? "App Guida offline — clicca per maggiori dettagli" : "Importa nuovi check-in dalla Guida Digitale"}
              className={`inline-flex items-center gap-2 text-white text-xs font-semibold px-3 py-1.5 rounded-md border transition ${
                guideStatus !== "online"
                  ? "bg-rose-500/30 hover:bg-rose-500/45 border-rose-300/50"
                  : "bg-white/15 hover:bg-white/25 border-white/25"
              } ${pullStatus.pulling ? "opacity-60 cursor-wait" : ""}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${pullStatus.pulling ? "animate-spin" : ""}`} />
              {pullStatus.pulling
                ? "Sincronizzo…"
                : guideStatus !== "online"
                  ? "Guida offline — riprova"
                  : "Aggiorna check-in dalla Guida"}
            </button>
            {guideStatus !== "online" && (
              <span className="text-[11px] text-rose-200 font-medium inline-flex items-center gap-1" data-testid="guide-offline-hint">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-300 animate-pulse" />
                La Guida non risponde — verifica il servizio
              </span>
            )}
            {pullStatus.lastPulledAt && !pullStatus.pulling && (
              <span className="text-[11px] text-white/60">
                Ultima sincronizzazione: {new Date(pullStatus.lastPulledAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/guide/backfill-bookings");
                  toast.success(
                    `Riconciliate ${data.matched} check-in → prenotazioni · ${data.enriched} prenotazioni arricchite (email, telefono, paese, documento).`,
                    { duration: 8000 },
                  );
                  refreshBookingsCount();
                } catch (err) {
                  toast.error(errMsg(err, "Riconciliazione fallita"));
                }
              }}
              className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white text-xs font-semibold px-3 py-1.5 rounded-md border border-white/25 transition"
              title="Riassegna i check-in dell'app guida alle prenotazioni Booking corrette (per data + cognome) e compila i campi mancanti."
              data-testid="backfill-bookings-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Riconcilia con Booking
            </button>
          </div>
        </div>
      </div>

      {/* === Banner "Revoche pendenti" (iter180) ===
          Mostrato quando ci sono prenotazioni annullate DOPO essere state
          comunicate ai portali ministeriali. L'host deve revocare
          manualmente sul portale e poi segnare come revocato. */}
      {/* iter186 — Banner "Revoche in sospeso" RIMOSSO su richiesta host.
          Le revoche ricomparivano continuamente. Se serve, si consulta
          direttamente la sezione "Revoche" del compliance. */}

      {/* === Codici di sincronizzazione (sempre disponibili, collassabili) === */}
      {webhookInfo && (
        <div
          className={`rounded-xl border ${
            webhookInfo.total_events_received === 0
              ? "bg-amber-50 border-amber-300"
              : "bg-white border-[#E8E2D9]"
          }`}
          data-testid="sync-codes-card"
        >
          <button
            type="button"
            onClick={() => setSyncCodesOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-3 p-4 text-left"
            aria-expanded={syncCodesOpen}
            data-testid="sync-codes-toggle"
          >
            <div className="flex items-center gap-3 min-w-0">
              <ExternalLink className={`w-5 h-5 shrink-0 ${webhookInfo.total_events_received === 0 ? "text-amber-700" : "text-[#1E3A5F]"}`} />
              <div className="min-w-0">
                <h4 className={`font-semibold ${webhookInfo.total_events_received === 0 ? "text-amber-900" : "text-[#1E3A5F]"}`}>
                  Codici di sincronizzazione App Guida
                </h4>
                <p className="text-[11px] mt-0.5 text-[#635F59]">
                  {webhookInfo.total_events_received === 0
                    ? "Non ancora configurato — clicca per vedere i codici"
                    : `Configurato · ${webhookInfo.total_events_received} eventi ricevuti`}
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-[#635F59] shrink-0 transition-transform ${syncCodesOpen ? "rotate-180" : ""}`} />
          </button>
          {syncCodesOpen && (
            <div className="px-4 pb-4 border-t border-amber-200 pt-3 space-y-2 text-xs">
              <p className="text-[12px] text-[#2D2926] leading-relaxed">
                Incolla questi 2 valori nel pannello admin della tua <strong>App Guida Digitale</strong> → sezione "Integrazioni / Webhook". Ogni check-in fatto dagli ospiti arriverà automaticamente nell'extranet e — se non c'è ancora una prenotazione — verrà creata una <strong>prenotazione walk-in</strong> da completare con camera e prezzo.
              </p>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#635F59]">Webhook URL (endpoint)</Label>
                <div className="flex items-center gap-2 bg-white border border-[#E8E2D9] rounded p-2 mt-1">
                  <span className="font-mono text-[12px] text-[#1E3A5F] truncate flex-1" data-testid="webhook-url-text">{webhookInfo.webhook_url}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(webhookInfo.webhook_url, "url")}
                    className="text-[#1E3A5F] hover:bg-[#FDFBF7] px-2 py-0.5 rounded border border-[#E8E2D9] text-[11px] font-semibold"
                    data-testid="copy-webhook-url"
                  >
                    {webhookCopied === "url" ? "✓ Copiato" : "Copia"}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#635F59]">Webhook Secret (firma)</Label>
                <div className="flex items-center gap-2 bg-white border border-[#E8E2D9] rounded p-2 mt-1">
                  <span className="font-mono text-[12px] text-[#1E3A5F] truncate flex-1" data-testid="webhook-secret-text">{webhookInfo.webhook_secret || "(non impostato)"}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(webhookInfo.webhook_secret, "secret")}
                    className="text-[#1E3A5F] hover:bg-[#FDFBF7] px-2 py-0.5 rounded border border-[#E8E2D9] text-[11px] font-semibold"
                    data-testid="copy-webhook-secret"
                  >
                    {webhookCopied === "secret" ? "✓ Copiato" : "Copia"}
                  </button>
                </div>
              </div>
              <details className="mt-2 text-[11px] text-[#635F59]">
                <summary className="cursor-pointer hover:text-[#1E3A5F]">Come si usa nell'App Guida?</summary>
                <div className="mt-2 pl-3 border-l-2 border-[#E8E2D9] space-y-1">
                  <div>1. Apri il pannello admin dell'App Guida Digitale</div>
                  <div>2. Vai in <strong>Integrazioni → Webhook</strong></div>
                  <div>3. Incolla l'<strong>URL</strong> nel campo "Endpoint webhook"</div>
                  <div>4. Incolla il <strong>Secret</strong> nel campo "Secret di firma"</div>
                  <div>5. Salva. Da quel momento ogni check-in / servizio extra arriverà qui in tempo reale.</div>
                </div>
              </details>
            </div>
          )}
        </div>
      )}

      {/* === Walk-in da assegnare — RIMOSSO da qui (15/05/26): ora gestito in
           "Prenotazioni" tramite filtro chip "Da assegnare". Lascio solo la
           modal di assegnazione in caso qualche flusso legacy la richiami. === */}

      {/* === Modal assegna camera === */}
      {walkinModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setWalkinModal(null); }}
          data-testid="assign-room-modal"
        >
          <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl">
            <h3 className="font-display text-xl text-[#1E3A5F] font-semibold mb-1">Assegna camera & tariffa</h3>
            <p className="text-xs text-[#635F59] mb-4">
              Ospite: <strong>{[walkinModal.guest?.first_name, walkinModal.guest?.last_name].filter(Boolean).join(" ") || "—"}</strong> · {walkinModal.check_in} → {walkinModal.check_out}
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Camera *</Label>
                <select
                  value={walkinModal._selected_room_id}
                  onChange={(e) => {
                    const rid = e.target.value;
                    const room = walkinRooms.find((rr) => rr.id === rid);
                    const rname = room ? (typeof room.name === "string" ? room.name : (room.name?.it || room.name?.en || room.id)) : "";
                    setWalkinModal({
                      ...walkinModal,
                      _selected_room_id: rid,
                      _selected_room_name: rname,
                      _price_per_night: room?.price_per_night || "",
                    });
                  }}
                  data-testid="assign-room-select"
                  className="mt-1 w-full h-10 border border-[#E8E2D9] rounded-md px-3 text-sm bg-white"
                >
                  <option value="">— Seleziona camera —</option>
                  {walkinRooms.map((r) => {
                    const rname = typeof r.name === "string" ? r.name : (r.name?.it || r.name?.en || r.id);
                    return (
                      <option key={r.id} value={r.id}>{rname}</option>
                    );
                  })}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Notti</Label>
                  <Input
                    type="number"
                    min="1"
                    value={walkinModal._nights}
                    onChange={(e) => setWalkinModal({ ...walkinModal, _nights: e.target.value })}
                    className="mt-1"
                    data-testid="assign-nights"
                  />
                </div>
                <div>
                  <Label className="text-xs">Ospiti</Label>
                  <Input
                    type="number"
                    min="1"
                    value={walkinModal._guests}
                    onChange={(e) => setWalkinModal({ ...walkinModal, _guests: e.target.value })}
                    className="mt-1"
                    data-testid="assign-guests"
                  />
                </div>
                <div>
                  <Label className="text-xs">€/notte</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={walkinModal._price_per_night}
                    onChange={(e) => setWalkinModal({ ...walkinModal, _price_per_night: e.target.value })}
                    className="mt-1"
                    data-testid="assign-price"
                  />
                </div>
              </div>
              <div className="bg-[#FAF7F2] rounded p-2 text-xs text-[#2D2926]">
                Totale stimato: <strong>€{((parseFloat(walkinModal._price_per_night || 0) || 0) * (parseInt(walkinModal._nights, 10) || 0)).toFixed(2)}</strong>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setWalkinModal(null)}
                className="px-3 py-1.5 text-sm text-[#635F59] hover:text-[#2D2926] rounded"
                data-testid="assign-cancel-btn"
              >
                Annulla
              </button>
              <Button
                onClick={submitAssignRoom}
                disabled={!walkinModal._selected_room_id}
                className="bg-[#AC5D45] hover:bg-[#9A5240] text-white font-semibold"
                data-testid="assign-confirm-btn"
              >
                Conferma assegnazione
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Webhook configuration banner — shown when no events have ever been received */}
      {webhookInfo && webhookInfo.total_events_received === 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="webhook-config-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-amber-900 mb-1">Webhook Guida Digitale non configurato</h4>
              <p className="text-sm text-amber-900 mb-3">
                I check-in fatti dalla Guida Digitale non arrivano qui automaticamente. Per riceverli devi configurare il <strong>webhook URL</strong> nella tua app guida.
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 bg-white border border-amber-200 rounded p-2">
                  <span className="font-mono text-amber-900 truncate flex-1">{webhookInfo.webhook_url}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(webhookInfo.webhook_url, "url")}
                    className="text-amber-700 hover:text-amber-900 px-2 py-0.5 rounded border border-amber-300 hover:bg-amber-100 text-[11px] font-semibold"
                    data-testid="copy-webhook-url"
                  >
                    {webhookCopied === "url" ? "✓ Copiato" : "Copia URL"}
                  </button>
                </div>
                <div className="flex items-center gap-2 bg-white border border-amber-200 rounded p-2">
                  <span className="font-mono text-amber-900 truncate flex-1">{webhookInfo.webhook_secret}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(webhookInfo.webhook_secret, "secret")}
                    className="text-amber-700 hover:text-amber-900 px-2 py-0.5 rounded border border-amber-300 hover:bg-amber-100 text-[11px] font-semibold"
                    data-testid="copy-webhook-secret"
                  >
                    {webhookCopied === "secret" ? "✓ Copiato" : "Copia Secret"}
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-amber-700 mt-2 leading-relaxed">
                <strong>Come configurare:</strong> apri il pannello admin della tua Guida Digitale → sezione "Integrazioni / Webhook" → incolla l'URL e il secret qui sopra → salva. Da quel momento, ogni check-in fatto dagli ospiti arriverà automaticamente qui.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Webhook events log — appare quando il webhook è configurato (>0 eventi) */}
      {webhookInfo && webhookInfo.total_events_received > 0 && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl" data-testid="webhook-events-log">
          <button
            type="button"
            onClick={() => setWebhookLogOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-2 p-4 hover:bg-[#FDFBF7] transition rounded-xl text-left"
            data-testid="webhook-log-toggle"
            aria-expanded={webhookLogOpen}
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <div>
                <h4 className="font-semibold text-[#1E3A5F] text-sm">Webhook Guida connesso</h4>
                <p className="text-[11px] text-[#635F59] mt-0.5">
                  Totale eventi: <strong>{webhookInfo.total_events_received}</strong>
                  {webhookInfo.last_event_received_at && (
                    <> · Ultimo: {new Date(webhookInfo.last_event_received_at).toLocaleString("it-IT")}</>
                  )}
                </p>
              </div>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-[#635F59] transition-transform ${webhookLogOpen ? "rotate-180" : ""}`}
            />
          </button>

          {webhookLogOpen && (
            <div className="px-5 pb-5 border-t border-[#E8E2D9] pt-3">
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={sendTestPing}
                  data-testid="send-test-ping-btn"
                  className="text-[11px] font-semibold px-2.5 py-1 rounded border border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100 transition inline-flex items-center gap-1"
                  title="Invia un evento test.ping al log per verificare il ricevitore webhook"
                >
                  <Send className="w-3 h-3" /> Invia test ping
                </button>
              </div>
              {recentEvents.length === 0 ? (
                <p className="text-xs text-[#635F59]">Nessun evento da mostrare.</p>
              ) : (
                <ul className="divide-y divide-[#E8E2D9] text-sm">
                  {recentEvents.map((ev, i) => (
                    <li key={ev.received_at + i} className="py-2 flex items-start gap-3">
                      <span
                        className={`shrink-0 mt-0.5 inline-flex text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                          ev.event === "guest.checkin"
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            : ev.event === "service.ordered"
                              ? "bg-blue-100 text-blue-800 border border-blue-200"
                              : ev.event === "test.ping"
                                ? "bg-purple-100 text-purple-800 border border-purple-200"
                                : "bg-slate-100 text-slate-700 border border-slate-200"
                        }`}
                      >
                        {ev.event}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-[#635F59]">
                          {new Date(ev.received_at).toLocaleString("it-IT")}
                        </div>
                        {ev.payload?.guest && (
                          <div className="text-xs text-[#2D2926] truncate">
                            Ospite: <strong>
                              {[ev.payload.guest.first_name, ev.payload.guest.last_name].filter(Boolean).join(" ") || "—"}
                            </strong>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Period picker — collassabile a tendina con date a tutta riga e preset su righe separate */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setPeriodPickerOpen((o) => !o)}
          aria-expanded={periodPickerOpen}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#FAF7F2] transition"
          data-testid="period-picker-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#AC5D45]/10 text-[#AC5D45] flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h4 className="font-display text-lg sm:text-xl text-[#1E3A5F] font-semibold leading-tight">
                Periodo da considerare
              </h4>
              <div className="text-xs text-[#635F59] mt-0.5">
                <strong className="text-[#1E3A5F]">{fromDate}</strong> → <strong className="text-[#1E3A5F]">{toDate}</strong>
              </div>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-[#635F59] shrink-0 transition-transform ${periodPickerOpen ? "rotate-180" : ""}`} />
        </button>
        {periodPickerOpen && (
          <div className="px-5 pb-5 border-t border-[#E8E2D9] pt-4 space-y-4">
            {/* Date inputs — riga intera */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#635F59] font-semibold">Da</Label>
                <DateInputIT value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="mt-1 h-11"
                  data-testid="compliance-from"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#635F59] font-semibold">A</Label>
                <DateInputIT value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="mt-1 h-11"
                  data-testid="compliance-to"
                />
              </div>
            </div>
            {/* Quick presets — Rapidi (mese) */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-blue-800 mb-2">📅 Rapidi (Mese)</div>
              <div className="flex flex-wrap gap-1.5">
                <PresetBtn variant="blue" label="Mese corrente" onClick={() => {
                  const d = new Date();
                  const start = new Date(d.getFullYear(), d.getMonth(), 1);
                  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                  setFromDate(fmtLocal(start));
                  setToDate(fmtLocal(end));
                }} />
                <PresetBtn variant="blue" label="Mese scorso" onClick={() => {
                  const d = new Date();
                  const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
                  const end = new Date(d.getFullYear(), d.getMonth(), 0);
                  setFromDate(fmtLocal(start));
                  setToDate(fmtLocal(end));
                }} />
                <PresetBtn variant="blue" label="Ultimi 30 gg" onClick={() => {
                  const d = new Date();
                  const start = new Date(d.getTime() - 30 * 86400000);
                  setFromDate(fmtLocal(start));
                  setToDate(TODAY());
                }} />
              </div>
            </div>
            {/* Trimestri Tassa Soggiorno (Modello A1) */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800 mb-2">🏛 Trimestri Tassa Soggiorno (Modello A1)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {[1, 2, 3, 4].map((q) => {
                  const y = new Date().getFullYear();
                  const ranges = ["gen–mar", "apr–giu", "lug–set", "ott–dic"];
                  return (
                    <PresetBtn key={`t${q}`} variant="amber" label={`T${q} · ${ranges[q - 1]}`} title={`Trimestre ${q} ${y}`} onClick={() => {
                      const start = new Date(y, (q - 1) * 3, 1);
                      const end = new Date(y, q * 3, 0);
                      setFromDate(fmtLocal(start));
                      setToDate(fmtLocal(end));
                    }} />
                  );
                })}
              </div>
            </div>
            {/* Anno fiscale MEF */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-purple-800 mb-2">📊 Anno fiscale MEF</div>
              <div className="grid grid-cols-2 gap-1.5">
                <PresetBtn variant="purple" label={`Anno ${new Date().getFullYear() - 1}`} title="Anno fiscale precedente (per MEF Annuale)" onClick={() => {
                  const y = new Date().getFullYear() - 1;
                  setFromDate(`${y}-01-01`);
                  setToDate(`${y}-12-31`);
                }} />
                <PresetBtn variant="purple" label={`Anno ${new Date().getFullYear()}`} title="Anno fiscale corrente" onClick={() => {
                  const y = new Date().getFullYear();
                  setFromDate(`${y}-01-01`);
                  setToDate(`${y}-12-31`);
                }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filtro mese arrivo + contatore prenotazioni nel periodo — card a parte */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        <div className="mt-0">
          <Label className="text-xs uppercase tracking-wider text-[#635F59]">Filtra per mese arrivo</Label>
          <select
            data-testid="month-filter"
            className="mt-1 w-full h-10 border border-[#E8E2D9] rounded-md px-3 text-sm bg-white"
            value={`${fromDate.slice(0, 7)}`}
            onChange={(e) => {
              const ym = e.target.value;
              if (!ym) return;
              const [y, m] = ym.split("-").map(Number);
              const start = new Date(y, m - 1, 1);
              const end = new Date(y, m, 0);
              setFromDate(fmtLocal(start));
              setToDate(fmtLocal(end));
            }}
          >
            {(() => {
              const opts = [];
              const now = new Date();
              for (let i = 0; i < 18; i++) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                const label = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
                opts.push(<option key={ym} value={ym}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>);
              }
              return opts;
            })()}
          </select>
        </div>

        {/* Counter prenotazioni nel periodo selezionato — aiuta a capire se "Scarica" produrrà file con dati. Cliccabile → porta alla tab Prenotazioni filtrata sul periodo. */}
        <button
          type="button"
          onClick={() => bookingsCount.eligible > 0 && onGoToBookings && onGoToBookings(fromDate)}
          disabled={bookingsCount.loading || bookingsCount.eligible === 0 || !onGoToBookings}
          className={`mt-4 w-full text-left p-3 rounded-lg flex items-center gap-2 text-sm transition ${
            bookingsCount.loading
              ? "bg-[#FAF7F2] text-[#635F59] cursor-wait"
              : bookingsCount.eligible > 0
                ? "bg-emerald-50 border border-emerald-200 text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300 cursor-pointer"
                : "bg-amber-50 border border-amber-200 text-amber-900 cursor-default"
          }`}
          data-testid="bookings-count-badge"
        >
          {bookingsCount.loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Conteggio prenotazioni nel periodo…
            </>
          ) : bookingsCount.eligible > 0 ? (
            <>
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span className="flex-1">
                <strong>{bookingsCount.eligible}</strong> prenotazion{bookingsCount.eligible === 1 ? "e" : "i"} confermat{bookingsCount.eligible === 1 ? "a" : "e"} nel periodo selezionato — i report conterranno questi dati.
              </span>
              <span className="text-xs underline opacity-80">Vedi prenotazioni →</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>
                Nessuna prenotazione confermata nel periodo selezionato. Scaricando otterrai un file vuoto: allarga il periodo o usa "Mese scorso".
              </span>
            </>
          )}
        </button>
      </div>

      {/* Lista clienti del periodo selezionato — collassabile a tendina con titolo grande */}
      {clientsList.length > 0 && (() => {
        // Pre-calculate counters for header summary (visible anche quando chiuso)
        const pendingPolizia = clientsList.filter((c) => !c.alloggiati_at).length;
        const pendingIstat = clientsList.filter((c) => !c.istat_at).length;
        const overduePolizia = clientsList.filter((c) => !c.alloggiati_at && c.checked_in_at && alloggiatiHoursLeft(c.checked_in_at) !== null && alloggiatiHoursLeft(c.checked_in_at) <= 0).length;
        return (
        <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden" data-testid="clients-list">
          {/* === Collapsible header — titolo grande con stato di sintesi === */}
          <button
            type="button"
            onClick={() => setClientsListOpen((v) => !v)}
            data-testid="clients-list-toggle"
            className={`w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition ${
              clientsListOpen ? "bg-[#FAF7F2]" : "bg-white hover:bg-[#FAF7F2]"
            }`}
            aria-expanded={clientsListOpen}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-full bg-[#AC5D45]/10 text-[#AC5D45] flex items-center justify-center shrink-0">
                <Folder className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-xl sm:text-2xl text-[#1E3A5F] font-semibold leading-tight">
                    Clienti nel periodo
                  </h3>
                  <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-[#AC5D45] text-white text-sm font-bold">
                    {clientsList.length}
                  </span>
                  {selectedCodes.size > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-[#7B3F2D] text-white text-sm font-bold border-2 border-white shadow"
                      title={`${selectedCodes.size} selezionati`}
                      data-testid="selected-count-badge"
                    >
                      ✓ {selectedCodes.size}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#635F59] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>📅 {fromDate} → {toDate}</span>
                  {overduePolizia > 0 && (
                    <span className="text-rose-700 font-semibold">⚠️ {overduePolizia} scaduto Polizia</span>
                  )}
                  {pendingPolizia > 0 && overduePolizia === 0 && (
                    <span className="text-amber-700 font-medium">⏰ {pendingPolizia} da Polizia</span>
                  )}
                  {pendingIstat > 0 && (
                    <span className="text-blue-700 font-medium">📊 {pendingIstat} da ISTAT</span>
                  )}
                  {pendingPolizia === 0 && pendingIstat === 0 && (
                    <span className="text-emerald-700 font-medium">✓ Tutto comunicato</span>
                  )}
                </div>
              </div>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-[#635F59] shrink-0 transition-transform ${clientsListOpen ? "rotate-180" : ""}`}
            />
          </button>

          {clientsListOpen && (
          <div className="px-5 pb-5 border-t border-[#E8E2D9]">
          {/* === Status header con DUE riepiloghi distinti per cadenza === */}
          {(() => {
            // Polizia: ogni cliente, scadenza 24h dal check-in
            const dueWithin24h = clientsList.filter((c) => !c.alloggiati_at && c.checked_in_at && alloggiatiHoursLeft(c.checked_in_at) !== null && alloggiatiHoursLeft(c.checked_in_at) > 0);
            const overdue = clientsList.filter((c) => !c.alloggiati_at && c.checked_in_at && alloggiatiHoursLeft(c.checked_in_at) !== null && alloggiatiHoursLeft(c.checked_in_at) <= 0);
            // ISTAT: aggregato mensile, vedi se TUTTI i clienti del mese sono comunicati
            const istatPending = clientsList.filter((c) => !c.istat_at);
            const monthLabel = new Date(`${fromDate}T00:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
            return (
              <div className="grid sm:grid-cols-2 gap-3 mt-4 mb-4">
                {/* POLIZIA — 24h */}
                <div
                  className={`rounded-lg p-3 border ${
                    overdue.length > 0
                      ? "bg-rose-50 border-rose-300"
                      : dueWithin24h.length > 0
                        ? "bg-amber-50 border-amber-300"
                        : "bg-emerald-50 border-emerald-200"
                  }`}
                  data-testid="alloggiati-status"
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#635F59]">
                    Polizia · Alloggiati Web (entro 24h)
                  </div>
                  {overdue.length > 0 ? (
                    <div className="mt-1 text-rose-800 font-semibold text-sm">
                      ⚠️ {overdue.length} client{overdue.length === 1 ? "e" : "i"} <strong>OLTRE</strong> le 24h dal check-in
                    </div>
                  ) : dueWithin24h.length > 0 ? (
                    <div className="mt-1 text-amber-800 font-semibold text-sm">
                      ⏰ {dueWithin24h.length} client{dueWithin24h.length === 1 ? "e" : "i"} da comunicare entro 24h
                    </div>
                  ) : (
                    <div className="mt-1 text-emerald-800 font-semibold text-sm">
                      ✓ Tutto comunicato a Polizia
                    </div>
                  )}
                  <div className="text-[11px] text-[#635F59] mt-0.5">D.M. 7/1/2013 — obbligo per ogni ospite</div>
                </div>
                {/* ISTAT — mensile */}
                <div
                  className={`rounded-lg p-3 border ${
                    istatPending.length > 0 ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-200"
                  }`}
                  data-testid="istat-status"
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#635F59]">
                    ISTAT / ROSS1000 (mensile · entro il 5 del mese successivo)
                  </div>
                  {istatPending.length > 0 ? (
                    <div className="mt-1 text-amber-800 font-semibold text-sm">
                      📅 {istatPending.length}/{clientsList.length} ancora da comunicare per <strong>{monthLabel}</strong>
                    </div>
                  ) : (
                    <div className="mt-1 text-emerald-800 font-semibold text-sm">
                      ✓ {monthLabel} comunicato a ISTAT
                    </div>
                  )}
                  <div className="text-[11px] text-[#635F59] mt-0.5">Reg. CE n. 692/2011 — riepilogo aggregato</div>
                </div>
              </div>
            );
          })()}

          <div className="flex items-center justify-between gap-2 mb-3 mt-2 flex-wrap">
            <div className="text-xs text-[#635F59] uppercase tracking-wider font-semibold">
              Seleziona clienti da marcare
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setSelectedCodes(new Set(clientsList.map((c) => c.id)))}
                className="text-[#1E3A5F] hover:underline"
                data-testid="clients-select-all"
              >
                Seleziona tutti
              </button>
              <span className="text-[#C9C2B6]">·</span>
              <button
                type="button"
                onClick={() => setSelectedCodes(new Set(clientsList.filter((c) => !c.alloggiati_at).map((c) => c.id)))}
                className="text-rose-700 hover:underline"
                data-testid="clients-select-pending-polizia"
                title="Seleziona solo i clienti non ancora comunicati a Polizia"
              >
                Solo da Polizia
              </button>
              <span className="text-[#C9C2B6]">·</span>
              <button
                type="button"
                onClick={() => setSelectedCodes(new Set())}
                className="text-[#635F59] hover:underline"
                data-testid="clients-deselect-all"
              >
                Deseleziona
              </button>
              <span className="text-[#635F59] ml-2">{selectedCodes.size}/{clientsList.length} sel.</span>
            </div>
          </div>
          <div className="grid gap-1.5 max-h-[480px] overflow-y-auto pr-1">
            {clientsList.map((c) => {
              const checked = selectedCodes.has(c.id);
              return (
                <label
                  key={`${c.kind}-${c.id}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition border ${
                    checked ? "bg-[#FAF7F2] border-[#E8C8B8]" : "bg-white border-transparent hover:bg-[#FAF7F2]"
                  }`}
                  data-testid={`client-row-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = new Set(selectedCodes);
                      if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                      setSelectedCodes(next);
                    }}
                    className="w-4 h-4 accent-[#AC5D45] cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1E3A5F] truncate flex items-center gap-1.5 flex-wrap">
                      {c.name}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            const r = await api.get(`/admin/reports/ricevute/single/${c.id}`, { responseType: "blob" });
                            const cd = r.headers?.["content-disposition"] || "";
                            const m = /filename="?([^";]+)"?/.exec(cd);
                            const fn = m ? m[1] : `ricevuta_${c.name.replace(/\s+/g, "_")}.pdf`;
                            const url = URL.createObjectURL(r.data);
                            const a = document.createElement("a");
                            a.href = url; a.download = fn;
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(url);
                            toast.success(`Ricevuta di ${c.name} scaricata`);
                          } catch {
                            toast.error("Errore generazione ricevuta");
                          }
                        }}
                        title="Scarica ricevuta singola PDF"
                        data-testid={`single-receipt-${c.id}`}
                        className="inline-flex items-center justify-center w-5 h-5 rounded text-[#1E3A5F] hover:bg-[#FAF7F2] hover:text-[#AC5D45]"
                      >
                        <ReceiptText className="w-3.5 h-3.5" />
                      </button>
                      {c.kind === "standalone" && (
                        <span className="text-[10px] uppercase bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded">walk-in</span>
                      )}
                      {/* Alloggiati 24h-deadline indicator (only for clients NOT yet communicated) */}
                      {!c.alloggiati_at && c.checked_in_at && (() => {
                        const h = alloggiatiHoursLeft(c.checked_in_at);
                        if (h === null) return null;
                        if (h <= 0) return (
                          <span className="text-[10px] uppercase bg-rose-600 text-white px-1.5 py-0.5 rounded font-bold animate-pulse" title="Già scaduta — comunicare URGENTEMENTE">
                            ⚠️ SCADUTA
                          </span>
                        );
                        if (h <= 6) return (
                          <span className="text-[10px] uppercase bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold" title="Scadenza 24h dal check-in">
                            ⏰ scade in {h}h
                          </span>
                        );
                        return (
                          <span className="text-[10px] uppercase bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded" title="Scadenza 24h dal check-in">
                            ⏰ {h}h
                          </span>
                        );
                      })()}
                      {c.alloggiati_at && (
                        <span className="text-[10px] uppercase bg-rose-100 text-rose-800 border border-rose-200 px-1.5 py-0.5 rounded" title={`Comunicato a Polizia il ${new Date(c.alloggiati_at).toLocaleDateString("it-IT")}`}>
                          ✓ POLIZIA
                        </span>
                      )}
                      {c.istat_at && (
                        <span className="text-[10px] uppercase bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded" title={`Comunicato a ISTAT il ${new Date(c.istat_at).toLocaleDateString("it-IT")}`}>
                          ✓ ISTAT
                        </span>
                      )}
                      {c.tassa_at && (
                        <span className="text-[10px] uppercase bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded" title={`Tassa versata il ${new Date(c.tassa_at).toLocaleDateString("it-IT")}`}>
                          ✓ TASSA
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#635F59] truncate">
                      📅 {c.arrival || "—"} {c.nights ? `· ${c.nights}n` : ""}
                      {c.country && ` · 🌍 ${c.country}`}
                      {c.doc && ` · 📄 ${c.doc}`}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Auto-marking: i bollini si applicano quando scarichi i report sotto */}
          <div className="mt-3 pt-3 border-t border-[#E8E2D9]">
            <p className="text-[11px] text-[#635F59] italic leading-relaxed">
              <strong>Automatico:</strong> appena scarichi un file dai 4 bottoni qui sotto, tutti i clienti del periodo vengono <strong>marcati automaticamente</strong> come comunicati a quell'ente (Polizia/ISTAT/Tassa). Niente da premere a mano.
            </p>
          </div>
          </div>
          )}
        </div>
        );
      })()}

      {/* Reports grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          const c = COLOR_MAP[r.color];
          const isLoading = downloading === r.key;
          return (
            <div
              key={r.key}
              data-testid={`report-card-${r.key}`}
              className={`rounded-xl border ${c.border} ${c.bg} p-5 transition`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-11 h-11 rounded-full ${c.icon} flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`font-display text-lg ${c.text} font-semibold leading-tight`}>{r.label}</h4>
                  <p className="text-xs text-[#635F59] mt-0.5">Formato: <strong>{r.format}</strong></p>
                </div>
              </div>
              <p className="text-sm text-[#2D2926] leading-relaxed mb-3">{r.desc}</p>
              <div className="flex items-start gap-2 text-[11px] text-[#635F59] mb-4 pl-1">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="italic">{r.legalNote}</span>
              </div>
              {/* Quick-pick buttons specifici per certi adempimenti */}
              {r.key === "tassa-soggiorno" ? (
                <div data-testid="a1-quick-grid">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 mb-1.5">Scegli il trimestre da generare</div>
                  <div className="grid grid-cols-4 gap-1.5 mb-1.5">
                    {[1, 2, 3, 4].map((q) => {
                      const y = new Date().getFullYear();
                      const monthNow = new Date().getMonth() + 1;
                      const currentQ = Math.ceil(monthNow / 3);
                      const isCurrent = q === currentQ;
                      // Trimestre da rendicontare = quello appena terminato. Es. se siamo a Maggio (Q2) → reporting su Q1.
                      const isReportable = q === ((currentQ - 2 + 4) % 4) + 1;
                      const setDatesAndDownload = () => {
                        const m1 = (q - 1) * 3 + 1;
                        const start = `${y}-${String(m1).padStart(2, "0")}-01`;
                        const lastDay = new Date(y, q * 3, 0).getDate();
                        const end = `${y}-${String(q * 3).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
                        setFromDate(start); setToDate(end);
                        setTimeout(() => downloadReport(r.key), 100);
                      };
                      return (
                        <Button
                          key={q}
                          onClick={setDatesAndDownload}
                          disabled={isLoading}
                          size="sm"
                          data-testid={`a1-q${q}`}
                          className={`h-9 text-xs font-bold transition-all ${
                            isReportable
                              ? "bg-amber-600 hover:bg-amber-700 text-white ring-2 ring-amber-300 ring-offset-1"
                              : isCurrent
                              ? "bg-amber-200 hover:bg-amber-300 text-amber-900 border border-amber-400"
                              : "bg-white hover:bg-amber-50 text-amber-800 border border-amber-300"
                          }`}
                          title={isReportable ? `T${q} è il trimestre da rendicontare ora` : isCurrent ? `T${q} è il trimestre in corso` : `Genera T${q}/${y}`}
                        >
                          T{q}
                          {isReportable && <span className="ml-0.5 text-[8px]">•</span>}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-[#635F59] text-center mb-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600 mr-1"></span>
                    Trimestre da rendicontare ora (evidenziato in arancio scuro)
                  </div>
                  {/* Fallback: download using custom date range */}
                  <Button
                    onClick={() => downloadReport(r.key)}
                    disabled={isLoading}
                    variant="outline"
                    size="sm"
                    data-testid={`btn-download-${r.key}`}
                    className="w-full h-8 text-xs border-amber-300 hover:bg-amber-50"
                  >
                    {isLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generazione…</> : <><Download className="w-3 h-3 mr-1" /> Usa range personalizzato dei filtri</>}
                  </Button>
                  {/* iter214 — link diretto al portale ufficiale del Comune RC */}
                  <a
                    href="https://tassadisoggiorno.reggiocal.it/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 w-full h-8 inline-flex items-center justify-center gap-1 rounded-md text-xs font-semibold bg-amber-700 hover:bg-amber-800 text-white transition-colors"
                    data-testid="a1-portal-rc-link"
                    title="Portale ufficiale Comune di Reggio Calabria per trasmettere il Modello A1"
                  >
                    <ExternalLink className="w-3 h-3" /> Vai al portale Comune RC (trasmissione A1)
                  </a>
                  <a
                    href="https://tassadisoggiorno.reggiocal.it/ComunicazioneModello21.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 w-full h-7 inline-flex items-center justify-center gap-1 rounded-md text-[10px] font-medium text-amber-800 hover:bg-amber-50 border border-amber-300"
                    data-testid="a1-portal-mef-link"
                    title="Modello 21 annuale al MEF (Ministero Economia e Finanze) — scadenza 30/06"
                  >
                    <ExternalLink className="w-3 h-3" /> Modello 21 annuale (MEF) — scadenza 30/06
                  </a>
                </div>
              ) : r.key === "mef-annual" ? (
                <div data-testid="mef-quick-grid">
                  <div className="text-[10px] uppercase tracking-wider text-purple-800 mb-1.5">Anno fiscale da dichiarare</div>
                  <div className="grid grid-cols-2 gap-1.5 mb-2">
                    {[new Date().getFullYear() - 1, new Date().getFullYear()].map((y, i) => {
                      const isCurrent = i === 0; // anno precedente = da dichiarare entro 30/06
                      const setDatesAndDownload = () => {
                        setFromDate(`${y}-01-01`); setToDate(`${y}-12-31`);
                        setTimeout(() => downloadReport(r.key), 100);
                      };
                      return (
                        <Button
                          key={y}
                          onClick={setDatesAndDownload}
                          disabled={isLoading}
                          size="sm"
                          data-testid={`mef-y${y}`}
                          className={`h-10 text-sm font-bold transition-all ${
                            isCurrent
                              ? "bg-purple-700 hover:bg-purple-800 text-white ring-2 ring-purple-300 ring-offset-1"
                              : "bg-white hover:bg-purple-50 text-purple-800 border border-purple-300"
                          }`}
                          title={isCurrent ? `Anno ${y} — da dichiarare entro 30/06/${y + 1}` : `Anno ${y} (in corso)`}
                        >
                          {y}{isCurrent && <span className="ml-1 text-[9px]">• da inviare</span>}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => downloadReport(r.key)}
                  disabled={isLoading}
                  data-testid={`btn-download-${r.key}`}
                  className="w-full bg-[#1E3A5F] hover:bg-[#152A45] text-white"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generazione...</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> Scarica {r.short}</>
                  )}
                </Button>
              )}
              {r.portal && (
                <a
                  href={r.portal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`btn-portal-${r.key}`}
                  className={`mt-2 w-full inline-flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium border ${c.border} bg-white/60 hover:bg-white ${c.text} transition`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {r.portal.label}
                </a>
              )}

              {/* Archivio: storico file generati per questa categoria */}
              <div className="mt-3 pt-3 border-t border-white/40">
                <button
                  type="button"
                  onClick={() => setExpandedArchive(expandedArchive === r.key ? null : r.key)}
                  data-testid={`archive-toggle-${r.key}`}
                  className={`w-full flex items-center justify-between text-xs font-semibold ${c.text} hover:opacity-80`}
                >
                  <span className="flex items-center gap-1.5">
                    <Folder className="w-3.5 h-3.5" />
                    Archivio file ({(archives[r.key] || []).length})
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${expandedArchive === r.key ? "rotate-180" : ""}`} />
                </button>
                {expandedArchive === r.key && (
                  <div className="mt-2 space-y-1" data-testid={`archive-list-${r.key}`}>
                    {(archives[r.key] || []).length === 0 ? (
                      <p className="text-[11px] text-[#635F59] italic py-1">
                        Nessun file ancora generato. Premi "Scarica" qui sopra: il file verrà archiviato qui per essere riscaricato in futuro.
                      </p>
                    ) : (
                      (archives[r.key] || []).map((f) => (
                        <div
                          key={f.id}
                          className="flex items-center gap-2 bg-white border border-[#E8E2D9] rounded px-2 py-1.5 text-xs"
                          data-testid={`archive-row-${f.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-[#1E3A5F] truncate" title={f.filename}>{f.filename}</div>
                            <div className="text-[10px] text-[#635F59]">
                              {new Date(f.generated_at).toLocaleString("it-IT")} · {Math.ceil(f.size / 1024)} KB · {f.from_date} → {f.to_date}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => downloadFromArchive(r.key, f)}
                            data-testid={`archive-download-${f.id}`}
                            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#FAF7F2]"
                            title="Scarica di nuovo"
                          >
                            <Download className="w-3.5 h-3.5 text-[#1E3A5F]" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteFromArchive(r.key, f)}
                            data-testid={`archive-delete-${f.id}`}
                            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded hover:bg-rose-50"
                            title="Elimina dall'archivio"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modello 21 abolito — info banner */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex gap-3 items-start" data-testid="modello-21-info">
        <Info className="w-5 h-5 text-slate-500 mt-0.5 shrink-0" />
        <div className="text-xs text-slate-700">
          <strong>Modello 21 (registro cartaceo) — ABOLITO 2026.</strong>
          <span className="text-slate-600"> L'obbligo del registro di gestione cartaceo è stato abolito dopo la sentenza della Corte di Cassazione 2026 che esclude i gestori di strutture ricettive dalla qualifica di agenti contabili. Nessun adempimento da gestire qui.</span>
        </div>
      </div>

      {/* Footer help */}
      <div className="text-xs text-[#635F59] text-center pt-2">
        💡 I file generati useranno i dati delle prenotazioni in stato <strong>confermato</strong> e <strong>completato</strong>, più tutti i <strong>check-in</strong> registrati nella Guida Digitale.
      </div>

      {/* Credentials vault */}
      <VaultCredentialsCard />
    </section>
  );
}

function PresetBtn({ label, onClick, variant = "neutral", title }) {
  const VARIANTS = {
    neutral: "border-[#E8E2D9] bg-white text-[#2D2926] hover:border-[#AC5D45] hover:text-[#AC5D45]",
    blue: "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 hover:border-blue-300",
    amber: "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:border-amber-400",
    purple: "border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100 hover:border-purple-300",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${VARIANTS[variant] || VARIANTS.neutral}`}
    >
      {label}
    </button>
  );
}


// ============================================================
//  PENDING REVOCATIONS BANNER (iter180)
//  Prenotazioni annullate DOPO essere state comunicate ai portali
//  ministeriali (Alloggiati Web / ISTAT). L'host deve revocare
//  manualmente sul portale e poi cliccare "Segna come revocato".
// ============================================================
function PendingRevocationsBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/compliance/pending-revocations");
      setData(r.data);
    } catch { setData({ items: [], count: 0 }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data || (data.count ?? 0) === 0) return null;

  const markRevoked = async (item, kind) => {
    const code = item.code || item.id;
    if (!code) return;
    setBusy(`${code}:${kind}`);
    try {
      await api.post("/admin/compliance/mark-revoked", { code, kind });
      toast.success(`Revoca ${kind} registrata per ${code}`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore nel salvataggio"));
    } finally { setBusy(null); }
  };

  const guestName = (it) =>
    it.guest_name
    || `${it.guest?.first_name || ""} ${it.guest?.last_name || ""}`.trim()
    || it.code
    || "Ospite";

  return (
    <div
      className="rounded-xl border-2 border-amber-400 bg-amber-50"
      data-testid="pending-revocations-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-amber-900">
              ⚠️ Revoche pendenti sui portali ministeriali
            </h4>
            <p className="text-[12px] mt-0.5 text-amber-800">
              <b>{data.count}</b> check-in annullati dopo comunicazione
              {data.count_alloggiati > 0 && (
                <> · Polizia/Alloggiati: <b>{data.count_alloggiati}</b></>
              )}
              {data.count_istat > 0 && (
                <> · ISTAT: <b>{data.count_istat}</b></>
              )}
              . Vai sul portale e revocali manualmente, poi clicca "Segna come revocato".
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-amber-700 shrink-0 transition ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-amber-300 divide-y divide-amber-200">
          {(data.items || []).map((it) => {
            const code = it.code || it.id;
            const needsA = it.compliance_alloggiati_needs_revocation && !it.compliance_alloggiati_revoked_at;
            const needsI = it.compliance_istat_needs_revocation && !it.compliance_istat_revoked_at;
            return (
              <div key={code} className="p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`revoke-item-${code}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-amber-900">
                    {guestName(it)}
                    <span className="ml-2 text-[11px] font-normal text-amber-700">· {code}</span>
                  </div>
                  <div className="text-[11px] text-amber-800 mt-0.5">
                    Soggiorno: {it.check_in || "—"} → {it.check_out || "—"}
                    {it.cancellation_status && <> · <b>{it.cancellation_status === "cancelled" ? "Annullato" : "No-show"}</b></>}
                    {it.cancelled_at && <> il {new Date(it.cancelled_at).toLocaleString("it-IT")}</>}
                  </div>
                  {it.cancellation_reason && (
                    <div className="text-[11px] text-amber-700 italic mt-0.5">"{it.cancellation_reason}"</div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 items-end">
                  {needsA && (
                    <div className="flex items-center gap-2">
                      <a
                        href="https://alloggiatiweb.poliziadistato.it/"
                        target="_blank" rel="noreferrer"
                        className="text-[11px] underline text-amber-800 inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> Alloggiati Web
                      </a>
                      <button
                        type="button"
                        onClick={() => markRevoked(it, "alloggiati")}
                        disabled={busy === `${code}:alloggiati`}
                        className="text-[11px] font-medium px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                        data-testid={`revoke-alloggiati-${code}`}
                      >
                        {busy === `${code}:alloggiati` ? "Salvo…" : "✓ Segna come revocato"}
                      </button>
                    </div>
                  )}
                  {needsI && (
                    <div className="flex items-center gap-2">
                      <a
                        href="https://ross1000.istat.it/"
                        target="_blank" rel="noreferrer"
                        className="text-[11px] underline text-amber-800 inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> ISTAT ROSS1000
                      </a>
                      <button
                        type="button"
                        onClick={() => markRevoked(it, "istat")}
                        disabled={busy === `${code}:istat`}
                        className="text-[11px] font-medium px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                        data-testid={`revoke-istat-${code}`}
                      >
                        {busy === `${code}:istat` ? "Salvo…" : "✓ Segna come revocato"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}